import { AtSign, Bell, CheckCircle2, MessageCircle, MessageSquare, UserRound } from 'lucide-react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { cn, relativeTime } from '@/features/issue'
import { useWorkspace } from '@/lib/workspace'

import { describeNotification } from './describe'
import type { Notification } from './types'

function iconFor(type: string): ReactNode {
  const className = 'size-4'
  switch (type) {
    case 'issue.assigned':
      return <UserRound className={className} aria-hidden="true" />
    case 'issue.transitioned':
      return <CheckCircle2 className={className} aria-hidden="true" />
    case 'comment.mentioned':
    case 'chat.mentioned':
      return <AtSign className={className} aria-hidden="true" />
    case 'comment.added':
      return <MessageSquare className={className} aria-hidden="true" />
    case 'chat.message':
      return <MessageCircle className={className} aria-hidden="true" />
    default:
      return <Bell className={className} aria-hidden="true" />
  }
}

/**
 * One notification. Clicking it marks it read and follows the link; the dot
 * on the right toggles read state without navigating.
 */
export function NotificationRow({
  notification,
  onOpen,
  onToggleRead,
  compact = false,
}: {
  notification: Notification
  onOpen: () => void
  onToggleRead: () => void
  compact?: boolean
}) {
  const workspace = useWorkspace()
  const navigate = useNavigate()
  const { actor, action, detail, path } = describeNotification(notification)
  const unread = !notification.read_at

  function open() {
    onOpen()
    if (path) navigate(workspace.path(path))
  }

  return (
    <div
      className={cn(
        'group flex items-start gap-3 rounded-md px-3 transition',
        compact ? 'py-2' : 'py-3',
        unread ? 'bg-brand-50/70 dark:bg-brand-950/40' : 'hover:bg-ink-50 dark:hover:bg-ink-900',
      )}
    >
      <button
        type="button"
        onClick={open}
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
        aria-label={`${actor} ${action}${detail ? `: ${detail}` : ''}`}
      >
        <span
          className={cn(
            'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full',
            unread ? 'bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200' : 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400',
          )}
        >
          {iconFor(notification.type)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm leading-snug text-ink-800 dark:text-ink-100">
            <span className="font-medium">{actor}</span> {action}
          </span>
          {detail ? (
            <span className={cn('mt-0.5 block truncate text-xs text-ink-500 dark:text-ink-400', !compact && 'whitespace-normal')}>
              {detail}
            </span>
          ) : null}
          <span className="mt-1 block text-[11px] text-ink-400">{relativeTime(notification.created_at)}</span>
        </span>
      </button>
      <button
        type="button"
        onClick={onToggleRead}
        aria-label={unread ? 'Mark as read' : 'Mark as unread'}
        title={unread ? 'Mark as read' : 'Mark as unread'}
        className="mt-2 flex size-5 shrink-0 items-center justify-center rounded-full opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
      >
        <span
          className={cn(
            'block size-2.5 rounded-full border',
            unread ? 'border-brand-600 bg-brand-600' : 'border-ink-300 dark:border-ink-600',
          )}
        />
      </button>
      {unread ? <span className="sr-only">Unread</span> : null}
    </div>
  )
}
